import { LitElement, css, html } from 'lit'
import { customElement, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js';
import type { KeysForType, WritablePart } from '../../common';

//keyof CSSStyleDeclaration

//const MASKED_CLASS = `masked`;
//const MASKED = `.${MASKED_CLASS}`;
const MASK = `input-mask`;
const MASK_CONTENT = `${MASK}--content`;

const strippedValue = (isCharsetPresent: boolean, value: string) =>
  isCharsetPresent ? value.replace(/\W/g, "") : value.replace(/\D/g, "");

const isInteger = (value: string) => !Number.isNaN(parseInt(value, 10));

const isLetter = (value: string) => (value ? value.match(/[A-Z]/i) : false);

// Used defined values
const maskedNumber = "_#dDmMyY9";
const maskedLetter = "A";

@customElement('ep-masked-input')
export class EPMaskedInput extends LitElement {

  @query('#mask')
  maskElement?: HTMLSpanElement;
  @query('slot[name=input]')
  slotElement?: HTMLSlotElement;
  get inputElement(): HTMLInputElement | undefined {
    return this.slottedElements.find(e => e instanceof HTMLInputElement) as HTMLInputElement;
  }
  get slottedElements(): Element[] {
    return this.slotElement?.assignedElements() ?? [];
  }

  get placeholder(): string | undefined {
    return this.inputElement?.dataset.placeholder;
  }
  set placeholder(val: string | undefined) {
    if (this.inputElement) this.inputElement.dataset.placeholder = val;
  }

  get value(): string {
    return this.inputElement?.value ?? '';
  }

  render() {
    return html`
      <!-- Shell -->
      <span class=${MASK} data-mask=${ifDefined(this.placeholder)}>
        <!-- Content -->
        <span class=${MASK_CONTENT} aria-hidden="true" id="mask">
          <i>${this.value}</i>
          ${this.placeholder?.substring(this.value.length)}
        </span>
        <!-- Input -->
        <slot name="input" @slotchange=${this._handleSlotChange} @input=${this._handleInput} @change=${this._handleChange}></slot>
      </span>
    `;
  }

  // Does some setup every time the slot is changed
  private _handleSlotChange() {
    if (this.slottedElements.length === 0) {
      console.warn(`[${this.tagName}] Could not find an ${HTMLInputElement.name} slotted. This component will not activate until one is slotted.`);
      return;
    }
    const input = this.inputElement;
    console.log('slotted:', input);
    if (input === undefined) {
      console.warn(`[${this.tagName}] Could not find an ${HTMLInputElement.name} slotted. This component will not activate until exactly one is slotted.`);
      return;
    }
    else if (this.slottedElements.length > 1) {
      console.warn(`[${this.tagName}] Multiple elements are slotted. This can make tracking 'input' events ambiguous. This component will not activate until exactly one is slotted.`);
      return;
    }

    // Based on: https://github.com/uswds/uswds/blob/1b88ad075a7cd3f4eab8a5828244c44995931fe9/packages/usa-input-mask/src/index.js
    
    const placeholder = input.getAttribute('placeholder') ?? undefined;
    if (placeholder) {
      this.placeholder = placeholder;
      input.setAttribute('maxlength', `${this.placeholder.length}`);
      input.setAttribute('data-placeholder', this.placeholder);
      input.removeAttribute('placeholder');
    }
    else {
      return;
    }

    this.requestUpdate();
    this._copyStylesToMask();
  }

  private _handleCurrentValue(el: HTMLInputElement) {
    const isCharsetPresent = el.dataset.charset;
    const placeholder = (isCharsetPresent || el.dataset.placeholder) ?? '';
    const { value } = el;
    const len = placeholder.length;
    let newValue = "";
    let i;
    let charIndex;

    const strippedVal = strippedValue(!!isCharsetPresent, value);

    for (i = 0, charIndex = 0; i < len; i += 1) {
      const isInt = isInteger(strippedVal[charIndex]);
      const isLet = isLetter(strippedVal[charIndex]);
      const matchesNumber = maskedNumber.indexOf(placeholder[i] as any) >= 0;
      const matchesLetter = maskedLetter.indexOf(placeholder[i] as any) >= 0;

      if (
        (matchesNumber && isInt) ||
        (isCharsetPresent && matchesLetter && isLet)
      ) {
        newValue += strippedVal[charIndex];
        charIndex += 1;
      } else if (
        (!isCharsetPresent && !isInt && matchesNumber) ||
        (isCharsetPresent &&
          ((matchesLetter && !isLet) || (matchesNumber && !isInt)))
      ) {
        return newValue;
      } else {
        newValue += placeholder[i];
      }
      // break if no characters left and the pattern is non-special character
      if (strippedVal[charIndex] === undefined) {
        break;
      }
    }

    return newValue;
  }

  /**
   * This isn't a super great solution for EVERY use case, but maybe it has potential
   */
  private _copyStylesToMask() {
    const mask = this.maskElement;
    const input = this.inputElement;
    if (!mask || !input) return;
    
    // Get styles of input element
    const styles = window.getComputedStyle(input);
    // Define the properties we want to copy over
    const props: KeysForType<WritablePart<CSSStyleDeclaration>, string>[] = [
      'fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight',
      'padding', 'margin',
      // TODO: add more?
    ];
    // Copy over the props
    for(let p of props) {
      mask.style[p] = styles[p];
    }
  }

  private _handleInput(event: InputEvent) {
    //console.log('input', event);
    // Confirm we are talking to who we think we're talking to
    if (event.target instanceof HTMLInputElement && this.inputElement === event.target) {
      // Constrains value that user entered
      event.target.value = this._handleCurrentValue(event.target);
      // Updates the mask
      this.requestUpdate();
    }
  }

  private _handleChange(event: Event) {
    //console.log('change', event);
  }

  static styles = css`
    :host {
      display: contents;
    }

    .input-mask {
      display: block;
      position: relative;
    }

    .input-mask--content, .masked {
      background-color: transparent;
      /* padding: 0.5rem; */
    }

    .input-mask--content {
      border: 1px solid transparent;
      color: #757575;
      display: block;
      left: 0px;
      pointer-events: none;
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
    }

    .input-mask--content i {
      visibility: hidden;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-masked-input': EPMaskedInput
  }
}
